import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { AccountInfo } from '@azure/msal-browser';
import { environment } from '@hub-env/environment';
import { Observable, tap } from 'rxjs';
import { FaroObservabilityService, normalizeUrlPath } from '@hub-services/faro-observability.service';
import { AuthService } from './auth.service';

// Extract a structured field from a backend error response body.
// The backend serializes errors as either:
//   { error: { trace_id, failure_reason, ... } }            (raw controller render)
//   { data: { error: { trace_id, failure_reason, ... } } }  (after TraceErrorResponseMiddleware)
//   { trace_id, failure_reason, ... }                       (top-level)
function firstNonEmptyString(...candidates: unknown[]): string | null {
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.length > 0) return candidate;
  }
  return null;
}

function readErrorField(body: any, key: string): string | null {
  if (!body || typeof body !== 'object') return null;
  if (typeof body[key] === 'string') return body[key];
  if (body.error && typeof body.error === 'object' && typeof body.error[key] === 'string') {
    return body.error[key];
  }
  if (body.data && typeof body.data === 'object') {
    if (typeof body.data[key] === 'string') return body.data[key];
    if (body.data.error && typeof body.data.error === 'object' && typeof body.data.error[key] === 'string') {
      return body.data.error[key];
    }
  }
  return null;
}

// HTTP interceptor that does not modify requests — only observes errors and
// reports them to Faro for outage triage. Kept separate from AuthInterceptor
// (which adds the Bearer token) so the responsibilities stay clear.
//
// Treats 401 (auth failed) and 403 (authn ok, authz denied) symmetrically:
// both indicate a user was rejected, and during an outage operators need to
// be able to list those users by email/oid regardless of which code came back.
@Injectable()
export class ApiTelemetryInterceptor implements HttpInterceptor {
  // AuthService is resolved lazily to avoid an Injector cycle with AuthInterceptor.
  private _authService: AuthService | null = null;

  constructor(private faro: FaroObservabilityService, private injector: Injector) {}

  private get authService(): AuthService | null {
    if (this._authService) return this._authService;
    try {
      this._authService = this.injector.get(AuthService);
    } catch {
      this._authService = null;
    }
    return this._authService;
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Absolute-prefix match on a compile-time origin, as in auth.interceptor.ts. A
    // stale origin here silently stops reporting; see environment.k8s.ts.
    if (!req.url.startsWith(environment.API_URL)) {
      return next.handle(req);
    }

    return next.handle(req).pipe(
      tap({
        error: (err: unknown) => {
          if (err instanceof HttpErrorResponse) {
            this.captureFailure(req, err);
          }
        },
      })
    );
  }

  private captureFailure(req: HttpRequest<any>, response: HttpErrorResponse): void {
    const status = response.status;
    if (status < 400) return;

    const body = response.error;
    const traceId = readErrorField(body, 'trace_id');
    const failureReason = readErrorField(body, 'failure_reason');
    const failureClass = readErrorField(body, 'failure_class');
    const detail = readErrorField(body, 'detail');

    // Normalized route (e.g. /api/v2/airlines/:id) for aggregation in Faro,
    // matching the convention TracingInstrumentation uses for span names.
    // Raw URL is still attached separately for per-request debugging.
    const route = normalizeUrlPath(req.url);

    if (status === 401 || status === 403) {
      const identity = this.activeAccountIdentity();
      this.faro.recordApiAuthFailure({
        traceId,
        status,
        url: req.urlWithParams,
        route,
        failureReason,
        failureClass,
        detail,
        accountEmail: identity.email,
        accountId: identity.localAccountId,
        accountTenantId: identity.tenantId,
        accountUsername: identity.username,
      });
    } else {
      this.faro.pushEvent('api_error', {
        'http.status_code': String(status),
        'http.method': req.method,
        'http.url': req.urlWithParams,
        'http.route': route,
        ...(traceId ? { 'trace_id': traceId } : {}),
      });
    }
  }

  // Pull whoever the browser currently believes is signed in. During an
  // auth outage this lets us locate every user whose API call 401'd/403'd
  // via the active MSAL account, independent of whether the (possibly stale)
  // Bearer token decoded successfully on the server.
  //
  // Routes through AuthService rather than MsalService directly — that's the
  // post-MSAL-review convention; AuthService.refreshAccounts() guarantees
  // getActiveAccount() returns the first cached account if no active is set.
  private activeAccountIdentity(): {
    email: string | null;
    localAccountId: string | null;
    tenantId: string | null;
    username: string | null;
  } {
    try {
      const svc = this.authService;
      const account: AccountInfo | null = svc?.getActiveAccount() ?? null;
      if (!account) return { email: null, localAccountId: null, tenantId: null, username: null };

      const claims = (account.idTokenClaims as any) || {};
      const email = firstNonEmptyString(
        claims.email,
        Array.isArray(claims.emails) ? claims.emails[0] : undefined,
        claims.preferred_username,
        account.username,
      );

      return {
        email,
        localAccountId: account.localAccountId || null,
        tenantId: account.tenantId || null,
        username: account.username || null,
      };
    } catch {
      return { email: null, localAccountId: null, tenantId: null, username: null };
    }
  }
}
